/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes, useMemo, useState } from 'preact/compat';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import '@/quote-management/components/TabbedContent/TabbedContent.css';

export interface TabbedContentProps extends HTMLAttributes<HTMLDivElement> {
  tabs: Map<string, string>;
  tabsContent: Map<string, VNode>;
  defaultActiveTab?: string;
}

export const TabbedContent: FunctionComponent<TabbedContentProps> = ({
  className,
  tabs,
  tabsContent,
  defaultActiveTab,
  ...props
}) => {
  const [activeTabId, setActiveTabId] = useState<string>(
    defaultActiveTab || Array.from(tabs.keys())[0]
  );

  const handleTabClick = (tabId: string) => {
    setActiveTabId(tabId);
  };

  const activeTabContent = useMemo(() => {
    return tabsContent.get(activeTabId);
  }, [activeTabId, tabsContent]);

  return (
    <div {...props} className={classes(['quote-management-tabbed-content', className])}>
      {tabs &&
        <div data-testid="tabbed-content-tabs" className={classes(['quote-management-tabbed-content__tabs'])}>
          {Array.from(tabs.entries()).map(([tabId, tabLabel]) => (
            <button
              className={
                classes([
                  'quote-management-tabbed-content__tab',
                  ['quote-management-tabbed-content__tab--active', tabId === activeTabId]
                ])}
              key={tabId}
              onClick={() => handleTabClick(tabId)}
            >
              {tabLabel}
            </button>
          ))}
        </div>
      }
      {
        activeTabContent &&
        <div className={classes(['quote-management-tabbed-content__active-tab-content'])}>
          <VComponent
            node={activeTabContent as VNode}
          />
        </div>
      }
    </div>
  );
};
